import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

// Allowlist of routes the AI is allowed to deep-link to.
// Prevents prompt-injection attacks that try to navigate to arbitrary URLs.
const ALLOWED_ROUTES = new Set([
  '/dashboard', '/evolution', '/victories', '/walking', '/fasting',
  '/meals', '/hydration', '/supplements', '/workouts', '/activities',
  '/recipes', '/my-recipes', '/grocery-list', '/planner',
  '/deals', '/monthly-report', '/messaging', '/settings',
  '/sos-craving', '/community', '/leaderboard', '/steps-ranking',
  '/coach-ai', '/guide',
]);

// Allowlist of query params per route. Only these keys are allowed
// through when the AI includes ?key=value in the deep-link token.
const ALLOWED_QUERY_PARAMS = {
  '/evolution':      new Set(['weight', 'tab', 'waist', 'chest', 'arms', 'neck', 'thighs', 'hips']),
  '/hydration':      new Set(['amount']),
  '/fasting':        new Set(['action', 'hours']),
  '/meals':          new Set(['action', 'text', 'meal_type', 'portions']),
  '/dashboard':      new Set(['action', 'text', 'meal_type', 'portions']),
  '/settings':       new Set(['tab']),
  '/supplements':    new Set(['action', 'name', 'dose']),
  '/workouts':       new Set(['action', 'type', 'duration', 'distance']),
  '/activities':     new Set(['action', 'type', 'duration']),
  '/planner':        new Set(['date', 'meal_type']),
  '/grocery-list':   new Set(['action', 'item', 'qty']),
  '/walking':        new Set(['goal']),
  '/coach-ai':       new Set(['auto']),
  '/sos-craving':    new Set(['action']),
};

// Sanitizes a raw path+query to only keep allowlisted params.
const sanitizeDeepLink = (rawPath) => {
  const [path, qs] = rawPath.split('?');
  if (!ALLOWED_ROUTES.has(path)) return null;
  if (!qs) return { path, search: '' };
  const allowedKeys = ALLOWED_QUERY_PARAMS[path] || new Set();
  const inParams = new URLSearchParams(qs);
  const outParams = new URLSearchParams();
  for (const [k, v] of inParams.entries()) {
    if (allowedKeys.has(k)) {
      // Hard length cap to avoid abusive payloads
      outParams.set(k, String(v).slice(0, 200));
    }
  }
  const search = outParams.toString();
  return { path, search: search ? `?${search}` : '' };
};

// Parses [[GO:/path|Label]] tokens from a message.
// Returns segments: { type: 'text' | 'link', content?: str, path?: str, label?: str }
const parseDeepLinks = (content) => {
  const segments = [];
  // Allow ?key=value&key2=value2 in the path portion
  const regex = /\[\[GO:(\/[A-Za-z0-9\-_/?&=%.,]+)\|([^\]]+)\]\]/g;
  let lastIndex = 0;
  let m;
  while ((m = regex.exec(content)) !== null) {
    const [full, rawPath, rawLabel] = m;
    const label = rawLabel.trim();
    if (m.index > lastIndex) {
      segments.push({ type: 'text', content: content.slice(lastIndex, m.index) });
    }
    const sanitized = sanitizeDeepLink(rawPath.trim());
    if (sanitized) {
      segments.push({ type: 'link', path: sanitized.path, search: sanitized.search, label });
    } else {
      // Unknown route → show as safe inline text instead of button
      segments.push({ type: 'text', content: `«${label}»` });
    }
    lastIndex = m.index + full.length;
  }
  if (lastIndex < content.length) {
    segments.push({ type: 'text', content: content.slice(lastIndex) });
  }
  return segments;
};

// Renders a message with inline text + deep-link buttons, preserving \n.
export default function CoachMessageContent({ content }) {
  const navigate = useNavigate();
  if (!content) return null;
  const segments = parseDeepLinks(content);

  // If no deep-links found, fall back to plain text with line breaks.
  if (!segments.some(s => s.type === 'link')) {
    return (
      <p className="text-sm whitespace-pre-wrap break-words" style={{ overflowWrap: 'anywhere' }} data-testid="coach-msg-text">
        {content}
      </p>
    );
  }

  // Split text segments by \n to keep formatting
  const renderText = (text, key) =>
    text.split('\n').map((line, i, arr) => (
      <React.Fragment key={`${key}-${i}`}>
        {line}
        {i < arr.length - 1 && <br />}
      </React.Fragment>
    ));

  return (
    <div className="text-sm min-w-0" data-testid="coach-msg-with-links">
      <p className="whitespace-pre-wrap break-words inline" style={{ overflowWrap: 'anywhere' }}>
        {segments.map((seg, i) =>
          seg.type === 'text' ? (
            <React.Fragment key={`t-${i}`}>{renderText(seg.content, `t-${i}`)}</React.Fragment>
          ) : null
        )}
      </p>
      <div className="mt-3 space-y-1.5">
        {segments
          .filter(seg => seg.type === 'link')
          .map((seg, i) => (
            <button
              key={`link-${i}`}
              onClick={() => navigate(`${seg.path}${seg.search || ''}`)}
              data-testid={`coach-deeplink-${seg.path.replace(/\//g, '-').slice(1)}`}
              className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg
                         bg-primary/10 border border-primary/40 hover:bg-primary/20
                         active:scale-[0.98] transition-all group overflow-hidden"
            >
              <div className="flex items-center gap-2 text-primary font-bold min-w-0 flex-1">
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 flex-shrink-0" />
                <span className="text-xs uppercase tracking-wide truncate">{seg.label}</span>
              </div>
              {/* Hide the raw path on narrow screens — it's mostly a debug
                  aid. On wider screens we show a truncated preview so long
                  deep-link payloads (e.g. full recipe text) can't push the
                  message bubble off-screen to the right. */}
              <span className="hidden sm:inline text-[10px] font-mono text-muted-foreground/60 truncate max-w-[45%] flex-shrink-0">
                {seg.path}{seg.search || ''}
              </span>
            </button>
          ))}
      </div>
    </div>
  );
}
